import {
  format as formatDate,
  parse as parseDateString,
  parseISO as _parseISO,
  isValid as isDateValid,
  isToday as isTodayDate,
  isPast as isPastDate,
  subHours as subtractHours,
  subSeconds as subtractSeconds,
  subYears as _subYears,
  formatDistance as formatDateDistance,
  formatDistanceToNow as _formatDistanceToNow,
  addDays as _addDays,
  getDaysInMonth as _getDaysInMonth,
  set as _set,
  differenceInDays as _differenceInDays,
  differenceInHours as _differenceInHours,
  differenceInMinutes as _differenceInMinutes,
  differenceInSeconds as _differenceInSeconds,
  differenceInMilliseconds as _differenceInMilliseconds,
  startOfDay as _startOfDay,
  getYear as _getYear,
  getMonth as _getMonth,
} from 'date-fns'

import { getTimezoneOffset as _getTimezoneOffset } from 'date-fns-tz'

/**
 * Returns current Date object
 * @return {Date}
 */
export const now = () => new Date()

/**
 *
 * @param params
 * @return {Date}
 */
export const newDate = (...params) => new Date(...params)

/**
 * Parse the given string in ISO 8601 format and return an instance of Date.
 * @param {string} dateString
 * @param {object} options
 * @param {boolean} options.withTime
 * @return {Date}
 *
 */
export const parseISO = (dateString, options) => {
  if (!options || !('withTime' in options)) {
    throw new Error('withTime option param is required')
  }

  if (typeof dateString !== 'string' || dateString.trim() === '') {
    return null
  }

  const raw = dateString.trim()

  const safeParse = (iso) => {
    try {
      const d = _parseISO(iso)
      return Number.isNaN(d?.getTime?.()) ? null : d
    } catch {
      return null
    }
  }

  if (options.withTime) {
    return safeParse(raw)
  }

  const datePart = raw.includes('T') ? raw.split('T')[0] : raw

  if (!datePart) return null

  return safeParse(`${datePart}T00:00:00`)
}

/**
 * Returns a string in simplified extended ISO format (ISO 8601),
 * which is always 24 or 27 characters long (YYYY-MM-DDTHH:mm:ss.sssZ or ±YYYYYY-MM-DDTHH:mm:ss.sssZ, respectively).
 * The timezone is always zero UTC offset, as denoted by the suffix "Z".
 * @param {Date} date
 * @param {object} options
 * @param {boolean} options.withTime
 * @return {string}
 */
export const toISOString = (date, options) => {
  if (!options || !('withTime' in options)) {
    throw new Error('withTime option param is required')
  }

  if (options.withTime) return date.toISOString()

  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()

  return new Date(Date.UTC(year, month, day, 0, 0, 0)).toISOString()
}

/**
 * Return the formatted date string in the given format.
 * @param {Date} date the original date
 * @param {string} format the string of tokens
 * @param {object} options an object with options
 * @return {string}
 * @link https://date-fns.org/v2.26.0/docs/format
 */
export const format = (date, format, options) => formatDate(date, format, options)

/**
 * Return the distance between the given dates in words.
 * @param {Date} date
 * @param {Date} baseDate
 * @param {object} options
 * @return {string}
 * @link https://date-fns.org/v2.27.0/docs/formatDistance
 * @example
 * const result = formatDistance(new Date(2014, 6, 2), new Date(2015, 0, 1))
 * //=> '6 months'
 */
export const formatDistance = (date, baseDate, options) =>
  formatDateDistance(date, baseDate, options)

/**
 * Return the distance between the given date and now in words.
 * @param {Date} date
 * @param {object=} options
 * @return {string}
 * @link https://date-fns.org/v2.27.0/docs/formatDistanceToNow
 * @example
 * const result = formatDistanceToNow(
 *   new Date(2014, 6, 2)
 * )
 * //=> '6 months'
 */
export const formatDistanceToNow = (date, options) => _formatDistanceToNow(date, options)

/**
 * Return the date parsed from string using the given format string.
 * @param {string} dateString
 * @param {string} formatString
 * @param {Date | number} referenceDate
 * @param {object} options
 * @return {Date}
 * @link https://date-fns.org/v2.27.0/docs/parse
 * @example
 * const result = parse('02/11/2014', 'MM/dd/yyyy', now())
 * //=> Tue Feb 11 2014 00:00:00
 */

export const parse = (dateString, formatString, referenceDate, options) =>
  parseDateString(dateString, formatString, referenceDate, options)

/**
 * Returns false if argument is Invalid Date and true otherwise
 * @param {Date | number} date
 * @return {boolean}
 * @link https://date-fns.org/v2.27.0/docs/isValid
 * @example
 * const result = isValid(new Date(2014, 1, 31))
 * //=> true
 */
export const isValid = (date) => isDateValid(date)

/**
 * Is the given date today?
 * @param {Date | number} date
 * @return {boolean}
 * @link https://date-fns.org/v2.27.0/docs/isToday
 * @example
 * const result = isToday(now())
 * //=> true
 */
export const isToday = (date) => isTodayDate(date)

/**
 * Is the given date in the past?
 * @param {Date | number} date
 * @return {boolean}
 * @link https://date-fns.org/v2.27.0/docs/isPast
 * @example
 * const result = isPast(new Date(2014, 6, 2))
 * //=> true
 */
export const isPast = (date) => isPastDate(date)

// Manipulation
// ----------------------------------------------------------------------
/**
 * Subtract the specified number of hours from the given date.
 * @param {Date} date
 * @param {number} hours
 * @return {Date}
 * @link https://date-fns.org/v2.27.0/docs/subHours
 * @example
 * const result = subHours(new Date(2014, 6, 11, 1, 0), 2)
 * //=> Thu Jul 10 2014 23:00:00
 */
export const subHours = (date, hours) => subtractHours(date, hours)

/**
 * Subtract the specified number of seconds from the given date.
 * @param {Date} date
 * @param {number} seconds
 * @return {Date}
 * @link https://date-fns.org/v2.27.0/docs/subSeconds
 * @example
 * const result = subSeconds(new Date(2014, 6, 10, 12, 45, 0), 30)
 * //=> Thu Jul 10 2014 12:44:30
 */
export const subSeconds = (date, seconds) => subtractSeconds(date, seconds)

/**
 * Subtract the specified number of years from the given date.
 * @param {Date} date
 * @param {number} years
 * @return {Date}
 * @link https://date-fns.org/v2.27.0/docs/subYears
 * @example
 * const result = subYears(new Date(2014, 8, 1), 5)
 * //=> Tue Sep 01 2009 00:00:00
 */
export const subYears = (date, years) => _subYears(date, years)

/**
 * Add the specified number of days to the given date.
 * @param {Date} date
 * @param {number} days
 * @return {Date}
 * @link https://date-fns.org/v2.27.0/docs/addDays
 * @example
 * const result = addDays(new Date(2014, 8, 1), 10)
 * //=> Thu Sep 11 2014 00:00:00
 */
export const addDays = (date, days) => _addDays(date, days)

/**
 * Set date values to a given date.
 * @param {Date} date
 * @param {object} values
 * @param {number=} values.year
 * @param {number=} values.month
 * @param {number=} values.date
 * @param {number=} values.hours
 * @param {number=} values.minutes
 * @param {number=} values.seconds
 * @param {number=} values.milliseconds
 * @return {Date}
 * @link https://date-fns.org/v2.27.0/docs/set
 * @example
 * const result = set(new Date(2014, 8, 20), { year: 2015, month: 9, date: 20 })
 * //=> Tue Oct 20 2015 00:00:00
 */
export const set = (date, values) => _set(date, values)

/**
 * Gets the number of days in a month of the given date.
 * @param {Date} date
 * @return {number}
 * @link https://date-fns.org/v2.27.0/docs/getDaysInMonth
 * @example
 * const result = getDaysInMonth(new Date(2000, 1))
 * //=> 29
 */
export const getDaysInMonth = (date) => _getDaysInMonth(date)

/**
 * Return the start of a day for the given date. The result will be in the local timezone.
 * @param {Date} date
 * @return {Date}
 * @link https://date-fns.org/v2.27.0/docs/startOfDay
 * @example
 * const result = startOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 00:00:00
 */
export const startOfDay = (date) => _startOfDay(date)

/**
 * Get the year of the given date.
 * @param {Date} date
 * @return {number}
 * @link https://date-fns.org/v2.27.0/docs/getYear
 * @example
 * const result = getYear(new Date(2014, 6, 2))
 * //=> 2014
 */
export const getYear = (date) => _getYear(date)

/**
 * Get the month of the given date
 * @param {Date} date
 * @return {number}
 * @link https://date-fns.org/v2.28.0/docs/getMonth
 */
export const getMonth = (date) => _getMonth(date)

// Difference
// ----------------------------------------------------------------------

/**
 * Get the number of full day periods between two dates.
 * @param {Date} dateLeft
 * @param {Date} dateRight
 * @return {number}
 * @link https://date-fns.org/v2.27.0/docs/differenceInDays
 * @example
 * const result = differenceInDays(
 *   new Date(2012, 6, 2, 0, 0),
 *   new Date(2011, 6, 2, 23, 0)
 * )
 * //=> 365
 */
export const differenceInDays = (dateLeft, dateRight) => _differenceInDays(dateLeft, dateRight)

/**
 * Get the number of hours between the given dates.
 * @param {Date} dateLeft
 * @param {Date} dateRight
 * @return {number}
 * @link https://date-fns.org/v2.27.0/docs/differenceInHours
 * @example
 * const result = differenceInHours(
 *   new Date(2014, 6, 2, 19, 0),
 *   new Date(2014, 6, 2, 6, 50)
 * )
 * //=> 12
 */
export const differenceInHours = (dateLeft, dateRight) => _differenceInHours(dateLeft, dateRight)

/**
 * Get the signed number of full (rounded towards 0) minutes between the given dates.
 * @param {Date} dateLeft
 * @param {Date} dateRight
 * @return {number}
 * @link https://date-fns.org/v2.27.0/docs/differenceInMinutes
 * @example
 * const result = differenceInMinutes(
 *   new Date(2014, 6, 2, 12, 20, 0),
 *   new Date(2014, 6, 2, 12, 7, 59)
 * )
 * //=> 12
 */
export const differenceInMinutes = (dateLeft, dateRight) =>
  _differenceInMinutes(dateLeft, dateRight)

/**
 * Get the number of seconds between the given dates.
 * @param {Date} dateLeft
 * @param {Date} dateRight
 * @return {number}
 * @link https://date-fns.org/v2.27.0/docs/differenceInSeconds
 * @example
 * const result = differenceInSeconds(
 *   new Date(2014, 6, 2, 12, 30, 20, 0),
 *   new Date(2014, 6, 2, 12, 30, 7, 999)
 * )
 * //=> 12
 */
export const differenceInSeconds = (dateLeft, dateRight) =>
  _differenceInSeconds(dateLeft, dateRight)

/**
 * Get the number of milliseconds between the given dates.
 * @param {Date} dateLeft
 * @param {Date} dateRight
 * @return {number}
 * @link https://date-fns.org/v2.27.0/docs/differenceInMilliseconds
 * @example
 * const result = differenceInMilliseconds(
 *   new Date(2014, 6, 2, 12, 30, 21, 700),
 *   new Date(2014, 6, 2, 12, 30, 20, 600)
 * )
 * //=> 1100
 */
export const differenceInMilliseconds = (dateLeft, dateRight) =>
  _differenceInMilliseconds(dateLeft, dateRight)

// Timezones
// ----------------------------------------------------------------------

/**
 * Returns the time zone offset from UTC time in milliseconds for IANA time zones as well as other time zone offset string formats.
 * @param {string} timeZone
 * @param {Date} date
 * @return {number}
 * @link https://www.npmjs.com/package/date-fns-tz#gettimezoneoffset
 * @example
 * const result = getTimezoneOffset('-07:00')
 * //=> -18000000 (-7 * 60 * 60 * 1000)
 * const result = getTimezoneOffset('Africa/Johannesburg')
 * //=> 7200000 (2 * 60 * 60 * 1000)
 * const result = getTimezoneOffset('America/New_York', new Date(2016, 0, 1))
 * //=> -18000000 (-5 * 60 * 60 * 1000)
 * const result = getTimezoneOffset('America/New_York', new Date(2016, 6, 1))
 * //=> -14400000 (-4 * 60 * 60 * 1000)
 */
export const getTimezoneOffset = (timeZone, date) => _getTimezoneOffset(timeZone, date)
// ----------------------------------------------------------------------
